import { skipToken } from '@reduxjs/toolkit/query/react';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { getToastMessage } from '@rsos/components/capstone/ConversationsToast/helpers';
import { SOURCE_TCAPI } from '@rsos/constants/locationSources';
import { CONVERSATION_START } from '@rsos/constants/streamWs/conversation';
import useIsTT911Enabled from '@rsos/hooks/useIsTT911Enabled';
import { conversationStateValues } from '@rsos/reduxDataStore/src/features/conversations/conversations.typings';
import {
  useCreateTCAPIConversationMutation,
  useGetTCAPIConversationMetadataQuery,
} from '@rsos/reduxDataStore/src/features/conversations/tcapiAPI';
import {
  setSelectedCallerID,
  StateWithEmergencyCalls,
} from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { setSelectedTab } from '@rsos/reduxDataStore/src/features/eventPane/eventPaneSlice';
import { useCurrentOrgName } from '@rsos/sinatra/src/selectors';
import { getMostRecentLoc } from '@rsos/utils/getMostRecentLoc';
import { dismissToastByID, notifyConversation } from '@rsos/utils/toastUtils';
import { RootState } from '../app';
import { logMissingTT911Capability } from '../utils/logMissingTT911Capability';

const useAllConversationToastNotifications = () => {
  const { callQueue, selectedCallerID, numberWithNewLocation } = useSelector(
    (state: StateWithEmergencyCalls) => state.emergencyCalls,
  );

  const conversations = useSelector((state: RootState) => state.conversations);

  const dispatch = useDispatch();
  const [createTCAPIConversation] = useCreateTCAPIConversationMutation();
  const orgName = useCurrentOrgName();
  const isTT911Enabled = useIsTT911Enabled();
  const customButtonSources = new Set([SOURCE_TCAPI]);

  // Defense in depth on top of the queue-entry subscription: keeps an RTK
  // Query subscriber alive at banner time so createTCAPIConversation's tag
  // invalidation triggers a refetch and flips the chat-render gate.
  const incomingConversation = numberWithNewLocation
    ? conversations[numberWithNewLocation]
    : undefined;
  useGetTCAPIConversationMetadataQuery(
    numberWithNewLocation &&
      orgName &&
      isTT911Enabled &&
      incomingConversation?.source === SOURCE_TCAPI
      ? { callerID: numberWithNewLocation, orgName }
      : skipToken,
  );

  // Sentry signal when a TCAPI conversation arrives without the capability —
  // one event per incoming caller, not per render.
  useEffect(() => {
    if (
      numberWithNewLocation &&
      incomingConversation?.source === SOURCE_TCAPI &&
      !isTT911Enabled
    ) {
      logMissingTT911Capability({
        callSite: 'useAllConversationToastNotifications',
        callerId: numberWithNewLocation,
        orgName,
        source: SOURCE_TCAPI,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only fire when the incoming caller changes
  }, [numberWithNewLocation]);

  const handleButtonClick = (conversation: conversationStateValues) => {
    if (conversation.source === SOURCE_TCAPI) {
      createTCAPIConversation({
        callerID: numberWithNewLocation,
        orgName: orgName,
      });
      dispatch(setSelectedCallerID(numberWithNewLocation));
      dispatch(setSelectedTab('chat'));
    }
  };

  // Show toast if a number comes in that is notifiable
  useEffect(() => {
    if (numberWithNewLocation) {
      const notifiedCall = callQueue[numberWithNewLocation];
      const mostRecentLocation = getMostRecentLoc(notifiedCall);
      const hasCustomButton = customButtonSources.has(
        mostRecentLocation?.source || '',
      );

      const conversation = conversations[numberWithNewLocation];

      // Suppress the TCAPI toast when the PSAP lacks the capability — the
      // Accept button would PUT successfully but the chat panel can't
      // render, so showing the banner just hands the operator a dead
      // button. The drift is logged separately by the Sentry effect above.
      if (conversation?.source === SOURCE_TCAPI && !isTT911Enabled) {
        return;
      }

      if (
        getToastMessage(conversation) &&
        conversation.recentMessageType === CONVERSATION_START
      ) {
        notifyConversation({
          conversation: conversation,
          ...(hasCustomButton && {
            handleButtonClick: () => handleButtonClick(conversation),
          }),
          options: {
            toastId: `conversations-toast-${numberWithNewLocation}`,
          },
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- numberWithNewLocation is persistent
  }, [numberWithNewLocation]);

  // Remove toast if its id matches selectedCallerID
  useEffect(() => {
    if (selectedCallerID) {
      dismissToastByID(`conversations-toast-${selectedCallerID}`);
    }
  }, [selectedCallerID]);
};

export default useAllConversationToastNotifications;
